import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  GetPCodeMaster,
  GetFCodeMaster,
  GetRCodeMaster,
  GetRCodePartsMaster,
  Status,
} from '../../services/apiTypes';
import {
  getPCodeMaster as getPCodeMasterAPI,
  getFCodeMaster as getFCodeMasterAPI,
  getRCodeMaster as getRCodeMasterAPI,
  getRCodePartsMaster as getRCodePartsMasterAPI,
  PFRCodeMasterSearchParam,
  RCodePartsMasterSearchParam,
} from './api';
import {
  isGetFCodeMaster,
  isGetPCodeMaster,
  isGetRCodeMaster,
  isGetRCodePartsMaster,
} from '../../_utils/typeGuardUtils';

interface MasterState {
  status: Status;
  pCodeList: GetPCodeMaster[];
  fCodeList: GetFCodeMaster[];
  rCodeList: GetRCodeMaster[];
  rCodePartsList: GetRCodePartsMaster[];
}

const initialState: MasterState = {
  status: 'idle',
  pCodeList: [],
  fCodeList: [],
  rCodeList: [],
  rCodePartsList: [],
};

export const getPCodeMaster = createAsyncThunk(
  'pCodeMaster/getPCodeMaster',
  async (params: PFRCodeMasterSearchParam, { rejectWithValue }) => {
    try {
      return await getPCodeMasterAPI(params);
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const getFCodeMaster = createAsyncThunk(
  'fCodeMaster/getFCodeMaster',
  async (params: PFRCodeMasterSearchParam, { rejectWithValue }) => {
    try {
      return await getFCodeMasterAPI(params);
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const getRCodeMaster = createAsyncThunk(
  'rCodeMaster/getRCodeMaster',
  async (params: PFRCodeMasterSearchParam, { rejectWithValue }) => {
    try {
      return await getRCodeMasterAPI(params);
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const getRCodePartsMaster = createAsyncThunk(
  'rCodePartsMaster/getRCodePartsMaster',
  async (params: RCodePartsMasterSearchParam, { rejectWithValue }) => {
    try {
      return await getRCodePartsMasterAPI(params);
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

const pfrCodeSlice = createSlice({
  name: 'codeMaster',
  initialState,
  reducers: {
    resetRCodePartsList: (state) => {
      state.rCodePartsList = initialState.rCodePartsList;
    },
    resetCodeMaster: (state) => {
      state.fCodeList = [];
      state.pCodeList = [];
      state.rCodeList = [];
      state.rCodePartsList = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getPCodeMaster.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(getPCodeMaster.fulfilled, (state, action) => {
        state.status = 'succeeded';
        if (isGetPCodeMaster(action.payload)) {
          state.pCodeList = action.payload;
        }
      })
      .addCase(getPCodeMaster.rejected, (state) => {
        state.status = 'failed';
      })
      .addCase(getFCodeMaster.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(getFCodeMaster.fulfilled, (state, action) => {
        state.status = 'succeeded';
        if (isGetFCodeMaster(action.payload)) {
          state.fCodeList = action.payload;
        }
      })
      .addCase(getFCodeMaster.rejected, (state) => {
        state.status = 'failed';
      })
      .addCase(getRCodeMaster.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(getRCodeMaster.fulfilled, (state, action) => {
        state.status = 'succeeded';
        if (isGetRCodeMaster(action.payload)) {
          state.rCodeList = action.payload;
        }
      })
      .addCase(getRCodeMaster.rejected, (state) => {
        state.status = 'failed';
      })
      .addCase(getRCodePartsMaster.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(getRCodePartsMaster.fulfilled, (state, action) => {
        state.status = 'succeeded';
        if (isGetRCodePartsMaster(action.payload)) {
          state.rCodePartsList = action.payload;
        }
      })
      .addCase(getRCodePartsMaster.rejected, (state) => {
        state.status = 'failed';
      });
  },
});

export default pfrCodeSlice.reducer;
export const { resetRCodePartsList, resetCodeMaster } = pfrCodeSlice.actions;
